<template>
	<Container class="settings">
		<!-- Basic Info -->
		<Card class="settings__section">
			<template #title>Basic Info</template>
			<template #content>
				<div class="settings__field">
					<label
						class="settings__label"
						for="app-name"
						>Name</label
					>
					<InputText
						id="app-name"
						v-model="name"
						class="settings__input" />
				</div>
				<div class="settings__field">
					<label
						class="settings__label"
						for="app-description"
						>Description</label
					>
					<Textarea
						id="app-description"
						v-model="description"
						class="settings__textarea"
						rows="4" />
				</div>
				<div class="settings__actions">
					<Button
						icon="fal fa-floppy-disk"
						label="Save changes"
						@click="saveBasicInfo" />
				</div>
			</template>
		</Card>

		<!-- Repository Config -->
		<Card class="settings__section">
			<template #title>
				<div class="settings__repo-title-container">
					<span>Repositories</span>
					<Button
						class="settings__repo-add"
						icon="fas fa-plus"
						label="Add Repository"
						severity="secondary"
						size="small"
						variant="outlined"
						@click="openAddRepoDialog" />
				</div>
			</template>
			<template #content>
				<div
					v-if="repos.length"
					class="settings__repo-list">
					<div
						v-for="rc in repos"
						:key="rc.id"
						class="settings__repo-card">
						<div class="settings__repo-header">
							<div class="settings__repo-title">
								<FontAwesomeIcon
									class="settings__repo-provider"
									icon="fab fa-github" />
								<span class="settings__repo-name">{{ getRepoName(rc.github_repository_url) }}</span>
							</div>

							<div class="settings__repo-actions">
								<Button
									icon="fal fa-pen"
									severity="secondary"
									size="small"
									text
									@click="editRepoConfig(rc.id)" />
								<Button
									icon="fal fa-trash"
									severity="danger"
									size="small"
									text
									@click="deleteRepoConfig(rc.id)" />
							</div>
						</div>
					</div>
				</div>
				<div
					v-else
					class="settings__repo-empty">
					<i
						aria-hidden="true"
						class="fal fa-box-open settings__repo-empty-icon"></i>
					<div>
						No repository configured.
						<span class="settings__repo-empty-sub"
							>Connect a repository to enable git code deliveries.</span
						>
					</div>
				</div>
			</template>
		</Card>

		<!-- Danger Zone -->
		<div class="settings__danger">
			<Button
				class="settings__archive-button"
				icon="fal fa-box-archive"
				variant="outlined"
				:label="detailsState.details.value?.archived ? 'Restore App' : 'Archive App'"
				:severity="detailsState.details.value?.archived ? 'success' : 'danger'"
				@click="toggleArchive" />
		</div>
		<RepositoryConfigForm
			v-if="detailsState.details.value?.id"
			:id="editingRepoId"
			as-dialog
			:force-values="{ app_id: Number(route.params.id) }"
			:hide-inputs="['app_id']"
			:should-redirect="false"
			:visible="repositoryConfigFormOpen"
			@close="repositoryConfigFormOpen = false"
			@created="handleRepositoryConfigUpdate()"
			@deleted="handleRepositoryConfigDelete()"
			@submit="repositoryConfigFormOpen = false"
			@updated="handleRepositoryConfigUpdate()" />
	</Container>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import Card from 'primevue/card'
import type { AppDetailsState } from '@/models/App/States'
import RepositoryConfigForm from '@/views/RepositoryConfig/components/Form.vue'
import { useToast } from 'primevue/usetoast'
import { useRoute } from 'vue-router'
import AppsApi from '@/models/App/Api'
import RepositoryConfigsApi from '@/models/RepositoryConfig/Api'
import { useConfirm } from 'primevue/useconfirm'
import Container from '@/components/Container.vue'
import type { RepositoryConfig } from '@/models/RepositoryConfig/Model'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const props = defineProps<{ detailsState: AppDetailsState }>()
const emit = defineEmits<{ (e: 'get-details'): void }>()

const detailsState = props.detailsState
const name = ref(detailsState.details.value?.name ?? '')
const description = ref(detailsState.details.value?.description ?? '')
const toast = useToast()
const route = useRoute()
const confirm = useConfirm()
const { repositoryConfigFormOpen, handleRepositoryConfigUpdate, handleRepositoryConfigDelete } =
	useRepositoryConfig()

watch(
	() => detailsState.details.value,
	() => {
		name.value = detailsState.details.value?.name ?? ''
		description.value = detailsState.details.value?.description ?? ''
	},
)

const repos = computed(() => detailsState.details.value?.repository_configs ?? [])
const editingRepoId = ref<RepositoryConfig['id'] | null>(null)

function getRepoName(url: string) {
	const match = (url ?? '').match(/github\.com\/(.+)$/)
	return match ? match[1] : url
}

async function saveBasicInfo() {
	await detailsState.update({ name: name.value, description: description.value })
	toast.add({ severity: 'success', summary: 'Saved', detail: 'Basic info updated', life: 2000 })
	emit('get-details')
}

function openAddRepoDialog() {
	editingRepoId.value = null
	repositoryConfigFormOpen.value = true
}

function editRepoConfig(id: RepositoryConfig['id']) {
	editingRepoId.value = id
	repositoryConfigFormOpen.value = true
}

function deleteRepoConfig(id: RepositoryConfig['id']) {
	confirm.require({
		message: 'Delete this repository config? This cannot be undone.',
		header: 'Are you sure?',
		icon: 'fas fa-exclamation-triangle',
		rejectLabel: 'Cancel',
		rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
		acceptProps: { label: 'Delete', severity: 'danger' },
		accept: async () => {
			await new RepositoryConfigsApi().destroy(id)
			emit('get-details')
		},
		reject: () => {},
	})
}

async function toggleArchive() {
	const id = detailsState.details.value?.id
	if (!id) return
	if (!detailsState.details.value?.archived) {
		confirm.require({
			message: `Are you sure you want to archive the app? You'll be able to restore it later.`,
			header: 'Are you sure?',
			icon: 'fas fa-exclamation-triangle',
			rejectLabel: 'Cancel',
			rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
			acceptProps: { label: 'Archive', severity: 'danger' },
			accept: async () => {
				await new AppsApi().destroy(Number(id))
				emit('get-details')
			},
			reject: () => {},
		})
	} else {
		confirm.require({
			message: `Are you sure you want to restore the app?`,
			header: 'Are you sure?',
			icon: 'fas fa-exclamation-triangle',
			rejectLabel: 'Cancel',
			rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
			acceptProps: { label: 'Restore', severity: 'success' },
			accept: async () => {
				await new AppsApi().update(Number(id), { archived: false })
				emit('get-details')
			},
			reject: () => {},
		})
	}
}

function useRepositoryConfig() {
	async function handleRepositoryConfigUpdate() {
		emit('get-details')
	}

	async function handleRepositoryConfigDelete() {
		emit('get-details')
	}

	const repositoryConfigFormOpen = ref<boolean>(false)

	return { handleRepositoryConfigUpdate, handleRepositoryConfigDelete, repositoryConfigFormOpen }
}
</script>

<style scoped lang="scss">
.settings {
	display: flex;
	flex-direction: column;
	gap: 16px;

	.settings__section {
		:deep(.p-card-title) {
			font-weight: 700;
		}
		.settings__field {
			display: flex;
			flex-direction: column;
			gap: 6px;
			margin-bottom: 12px;

			.settings__label {
				font-weight: 600;
			}

			.settings__input,
			.settings__textarea {
				width: 100%;
			}
		}

		.settings__actions {
			display: flex;
			justify-content: flex-end;
			margin-top: 8px;
		}

		.settings__repo-title-container {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		.settings__repo-list {
			padding-top: 16px;
			display: flex;
			flex-direction: column;
			gap: 16px;

			.settings__repo-card {
				border-radius: 14px;
				padding: 16px;
				background: linear-gradient(180deg, var(--p-surface-0), var(--p-surface-0));
				border: 1px solid var(--p-surface-200);
				box-shadow:
					0 1px 2px rgba(0, 0, 0, 0.05),
					0 8px 24px rgba(0, 0, 0, 0.05);

				@media all {
					background: linear-gradient(180deg, var(--p-surface-900), var(--p-surface-900));
					border-color: var(--p-surface-800);
					box-shadow:
						0 1px 2px rgba(0, 0, 0, 0.4),
						0 8px 24px rgba(0, 0, 0, 0.25);
				}

				.settings__repo-header {
					display: flex;
					justify-content: space-between;
					align-items: center;

					.settings__repo-title {
						display: flex;
						align-items: center;
						gap: 8px;

						.settings__repo-provider {
							font-size: 22px;
							color: var(--p-text-color);
						}

						.settings__repo-name {
							font-weight: 700;
						}
					}

					.settings__repo-actions {
						display: flex;
						gap: 6px;
					}
				}
			}
		}
	}

	.settings__repo-empty {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 14px;
		border-radius: 10px;
		border: 1px dashed var(--p-surface-300);
		background: var(--p-surface-50);
		color: var(--p-text-secondary);

		@media all {
			background: var(--p-surface-950);
			border-color: var(--p-surface-700);
		}

		.settings__repo-empty-icon {
			font-size: 18px;
		}

		.settings__repo-empty-sub {
			display: block;
			font-size: 12px;
			opacity: 0.8;
		}
	}

	.settings__danger {
		margin-top: 12px;

		.settings__archive-button {
			width: 100%;
		}
	}
}
</style>
